import * as React from 'react';
import { fetchItemsIfNeeded, getItem } from '@/redux/modules/item';
import { fetchMoreSearchResults, getSearchedItemIds, getSearchUiState, submitSearch } from '@/redux/modules/search';
import { fetchMoreSimilarItems, getMoreResultsAvailable, getSimilarItemIds } from '@/redux/modules/similarItemsV3';
import { useDispatch, useSelector } from 'react-redux';
import { CategoryLink } from '@/types/CategoryLink';
import { getCatalog } from '@/redux/modules/catalog';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { getMostPopularItemsByBids } from '@/redux/modules/filteredItems';
import isEqual from 'react-fast-compare';
import { ItemFacets } from '@/types/ItemFacets';
import SimilarItems from './SimilarItems';
import useWindowSize from '@/hooks/useWindowSize';

type Props = {
    categories?: boolean;
    categoryLinks?: CategoryLink[];
    centerHeader?: boolean;
    className?: string;
    itemId: number;
    suggestions?: any[];
    title?: React.ReactNode;
    useSimilarItemCard?: boolean;
};

const SIMILAR_ITEMS = 'similarItems';

const SimilarItemsContainer = (props: Props) => {
    const {
        categories,
        categoryLinks = [],
        centerHeader = false,
        className,
        itemId,
        suggestions = [],
        title,
        useSimilarItemCard = false,
    } = props;

    const dispatch = useDispatch();

    const [catIds, setCatIds] = React.useState([]);
    const [name, setName] = React.useState(SIMILAR_ITEMS);
    const [page, setPage] = React.useState(1);

    const item = useSelector((state) => getItem(state, itemId), isEqual);
    const { catalogId, title: itemTitle } = item;
    const { catalogStatus } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { width } = useWindowSize();
    const facets: ItemFacets = useSelector((state) => getItemFacets(state, itemId), isEqual);

    let limit = width < 991 ? 12 : 15;
    const similarItemIds = useSelector((state) => getSimilarItemIds(state, itemId, limit), isEqual);
    const categoryIds = categories && facets.categories ? facets.categories.map((i) => i.categoryId) : [];
    const moreSimilarResultsAvailable = useSelector(getMoreResultsAvailable, isEqual);

    const tagItemIds = useSelector(getSearchedItemIds, isEqual);
    const { isLoading, totalPages } = useSelector(getSearchUiState, isEqual);

    React.useEffect(() => {
        dispatch(fetchItemsIfNeeded(tagItemIds));
    }, [dispatch, tagItemIds]);

    const popularItemIds = useSelector((state) => getMostPopularItemsByBids(state, item.sellerId), isEqual);

    const getMoreSimilarItems = () => {
        if (name === SIMILAR_ITEMS) {
            dispatch(fetchMoreSimilarItems({ categoryIds, itemId, sellerId: item.sellerId, title: itemTitle }));
        } else {
            let nextPage = page + 1;
            dispatch(
                fetchMoreSearchResults(
                    {
                        categories: catIds,
                        options: { status: ['upcoming', 'live', 'online'] },
                        page: nextPage,
                        pageSize: 12,
                    },
                    'item'
                )
            );
            setPage(nextPage);
        }
    };

    const getTagItems = (categoryIds: any[], title: string) => {
        dispatch(
            submitSearch(
                {
                    categories: categoryIds,
                    options: { status: ['upcoming', 'live', 'online'] },
                    page: 1,
                    pageSize: 12,
                },
                'item'
            )
        );

        setCatIds(categoryIds);
        setName(title);
        setPage(1);
    };

    const selectedItems = name !== SIMILAR_ITEMS ? tagItemIds : similarItemIds;
    const moreResultsAvailable = name !== SIMILAR_ITEMS ? page < totalPages : moreSimilarResultsAvailable;

    return (
        <SimilarItems
            catalogStatus={catalogStatus}
            categoryLinks={categoryLinks}
            centerHeader={centerHeader}
            className={className}
            facets={facets}
            getMoreSimilarItems={getMoreSimilarItems}
            getTagItems={getTagItems}
            isLoading={isLoading && name !== SIMILAR_ITEMS}
            itemTitle={itemTitle}
            moreResultsAvailable={moreResultsAvailable}
            popularItemIds={popularItemIds}
            selectedItems={selectedItems}
            suggestions={suggestions}
            title={title}
            useSimilarItemCard={useSimilarItemCard}
            width={width}
        />
    );
};

export default React.memo<Props>(SimilarItemsContainer, isEqual);
